import React, {
	useState,
	useCallback,
	useEffect,
	useContext,
	useRef,
} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Box,
	Typography,
	Stack,
	Paper,
	Grid,
	Checkbox,
	FormControlLabel,
	List,
	ListItem,
	ListItemText,
	Autocomplete,
	TextField,
	Alert,
	ToggleButton,
	ToggleButtonGroup,
	Chip,
	Divider,
	InputAdornment,
	Tooltip,
	useTheme,
	Fade,
	ListItemIcon,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
	Close as CloseIcon,
	PhotoLibrary as PhotoLibraryIcon,
	PictureAsPdf as PictureAsPdfIcon,
	CloudUpload as CloudUploadIcon,
	ArrowBack as ArrowBackIcon,
	Delete as DeleteIcon,
	ImageSearch as ImageSearchIcon,
	AddCircleOutline as AddCircleOutlineIcon,
	VideoLibrary as VideoLibraryIcon,
	TextFields as TextFieldsIcon,
	Settings as SettingsIcon,
	Print as PrintIcon,
	Restaurant as RestaurantIcon,
	ConfirmationNumber as TicketIcon,
	UploadFile as UploadFileIcon,
	Folder as FolderIcon,
	Style as StyleIcon,
	Tune as TuneIcon,
	Storefront as StorefrontIcon,
	ShoppingCart as ArticleIcon,
	PriorityHigh as PriorityIcon,
	People as QueueIcon,
} from "@mui/icons-material";
import { contentSetApi, mediaApi } from "../apiUser";
import { InfoContext } from "../context/InfoContext";
import WaitingDialog from "./WaitingDialog";

// --- SOTTOCOMPONENTI UI ---

// 1. Selection Card
const SelectionCard = ({
	icon,
	title,
	description,
	onClick,
	color = "primary",
}) => {
	const theme = useTheme();
	return (
		<Paper
			elevation={0}
			onClick={onClick}
			sx={{
				p: 3,
				height: "100%",
				cursor: "pointer",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				textAlign: "center",
				borderRadius: 3,
				border: "1px solid",
				borderColor: theme.palette.divider,
				transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
				"&:hover": {
					borderColor: `${color}.main`,
					backgroundColor: alpha(theme.palette[color].main, 0.04),
					transform: "translateY(-4px)",
					boxShadow: `0 12px 24px -10px ${alpha(theme.palette[color].main, 0.2)}`,
				},
			}}
		>
			<Box
				sx={{
					p: 2,
					borderRadius: "50%",
					mb: 2,
					bgcolor: alpha(theme.palette[color].main, 0.1),
					color: `${color}.main`,
				}}
			>
				{React.cloneElement(icon, { fontSize: "large" })}
			</Box>
			<Typography variant="h6" fontWeight={700}>
				{title}
			</Typography>
			{description && (
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					{description}
				</Typography>
			)}
		</Paper>
	);
};

// 2. Dropzone
function Dropzone({ onDrop, accept, isPdf = false, inputRef, maxFiles = 0 }) {
	const [isDragging, setIsDragging] = useState(false);
	const theme = useTheme();

	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};
	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};
	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
		onDrop(e.dataTransfer.files);
	};

	return (
		<Box
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDragOver={handleDragOver}
			onDrop={handleDrop}
			onClick={() => inputRef.current && inputRef.current.click()}
			sx={{
				border: "2px dashed",
				borderColor: isDragging ? "primary.main" : theme.palette.divider,
				borderRadius: 4,
				backgroundColor: isDragging
					? alpha(theme.palette.primary.main, 0.08)
					: alpha(theme.palette.action.hover, 0.3),
				p: 6,
				textAlign: "center",
				cursor: "pointer",
				transition: "all 0.2s ease-in-out",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				minHeight: 250,
			}}
		>
			<input
				ref={inputRef}
				type="file"
				accept={accept}
				multiple={maxFiles !== 1}
				style={{ display: "none" }}
				onChange={(e) => onDrop(e.target.files)}
			/>
			<Box
				sx={{
					width: 80,
					height: 80,
					borderRadius: "50%",
					mb: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: alpha(theme.palette.primary.main, 0.1),
					color: "primary.main",
				}}
			>
				<CloudUploadIcon sx={{ fontSize: 40 }} />
			</Box>
			<Typography variant="h6" fontWeight={700} gutterBottom>
				{isDragging ? "Rilascia i file qui" : "Clicca o Trascina i file"}
			</Typography>
			<Typography variant="body2" color="text.secondary">
				{isPdf
					? "Supportati: Solo PDF"
					: maxFiles === 1
						? "Carica un singolo file"
						: "Caricamento multiplo supportato"}
			</Typography>
		</Box>
	);
}

// 3. Section Box
const SectionBox = ({ title, icon, children }) => {
	const theme = useTheme();
	return (
		<Paper
			elevation={0}
			sx={{
				p: 2.5,
				mb: 2,
				borderRadius: 3,
				border: `1px solid ${theme.palette.divider}`,
				bgcolor: alpha(theme.palette.background.default, 0.5),
			}}
		>
			<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
				<Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
				<Typography
					variant="subtitle2"
					fontWeight={700}
					sx={{
						textTransform: "uppercase",
						letterSpacing: 0.5,
						color: "text.secondary",
					}}
				>
					{title}
				</Typography>
			</Stack>
			{children}
		</Paper>
	);
};

// --- COMPONENTE PRINCIPALE ---

export default function Uploader({
	open,
	onClose,
	onSave,
	mediaMode = "MEDIA",
}) {
	const theme = useTheme();
	const { info } = useContext(InfoContext);
	const userInfos = info.selectedClient;
	const clientId = userInfos?.client_id;

	// Stati Generali
	const [step, setStep] = useState("contentSet");
	const [contentSets, setContentSets] = useState([]);
	const [selectedContentSet, setSelectedContentSet] = useState(null);
	const [showNewContentSet, setShowNewContentSet] = useState(false);
	const [newContentSetName, setNewContentSetName] = useState("");
	const [files, setFiles] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState("");
	const [error, setError] = useState(null);

	// Stati PDF/Image (Monitor)
	const [pdfFile, setPdfFile] = useState(null);
	const [extractedImages, setExtractedImages] = useState([]);
	const [selectedImages, setSelectedImages] = useState([]);

	// Stati Extra
	const [extraType, setExtraType] = useState(null);
	const [extraText, setExtraText] = useState("");
	const [extraConfig, setExtraConfig] = useState({
		trigger: 1,
		counters: [],
		articles: [],
		priority: 5,
		queueThreshold: 0,
	});

	const inputRef = useRef(null);

	// --- API & EFFECTS ---
	const getContentSets = async () => {
		if (!clientId) return;
		setIsLoading(true);
		setLoadingMessage("Caricamento raccolte...");
		try {
			const response = await contentSetApi.get(mediaMode);
			setContentSets(response.data || []);
		} catch (err) {
			setError("Impossibile caricare i content set.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			getContentSets();
			resetState();
		}
	}, [open, clientId]);

	const resetState = () => {
		setStep("contentSet");
		setFiles([]);
		setPdfFile(null);
		setExtractedImages([]);
		setSelectedImages([]);
		setSelectedContentSet(null);
		setShowNewContentSet(false);
		setNewContentSetName("");
		setError(null);
		setExtraType(null);
		setExtraText("");
		setExtraConfig({
			trigger: 1,
			counters: [],
			articles: [],
			priority: 5,
			queueThreshold: 0,
		});
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	// --- HANDLERS ---
	const handleCreateContentSet = async () => {
		if (!newContentSetName.trim()) return;
		setIsLoading(true);
		setLoadingMessage("Creazione set...");
		try {
			const response = await contentSetApi.create({
				content_set_name: newContentSetName,
				content_set_description: newContentSetName,
				set_target: mediaMode,
			});
			const newSet = response.data;
			setContentSets((prev) => [...prev, newSet]);
			setSelectedContentSet(newSet);
			setShowNewContentSet(false);
			setNewContentSetName("");
		} catch (err) {
			setError("Errore creazione set.");
		} finally {
			setIsLoading(false);
		}
	};

	const handleFilesDrop = useCallback(
		(droppedFiles) => {
			if (mediaMode === "EXTRA" && extraType === "image")
				setFiles([droppedFiles[0]]);
			else setFiles((prev) => [...prev, ...Array.from(droppedFiles)]);
		},
		[mediaMode, extraType],
	);

	// --- HANDLERS PDF (LOGICA VECCHIA RIPRISTINATA) ---

	const handlePdfSelected = (droppedFiles) => {
		if (droppedFiles.length > 0) {
			setPdfFile(droppedFiles[0]);
			setStep("pdfOptions");
		}
	};

	const handlePdfExtract = async (extractType) => {
		setIsLoading(true);
		setError(null);
		setLoadingMessage("Elaborazione PDF...");
		try {
			const response = await mediaApi.extractFromPdf(extractType, pdfFile);
			const base64Images = response.data.images || [];

			// Logica originale di conversione
			const imageObjects = base64Images.map((b64, index) => {
				const byteString = atob(b64);
				const ab = new ArrayBuffer(byteString.length);
				const ia = new Uint8Array(ab);
				for (let i = 0; i < byteString.length; i++) {
					ia[i] = byteString.charCodeAt(i);
				}
				const blob = new Blob([ab], { type: "image/png" });
				const file = new File([blob], `extracted_image_${index + 1}.png`, {
					type: "image/png",
				});
				return {
					id: `extracted_${index}`,
					url: URL.createObjectURL(file),
					file: file,
				};
			});

			setExtractedImages(imageObjects);
			setStep("pdfPreview");
		} catch (err) {
			console.error(err);
			setError("Errore durante l'estrazione PDF.");
			setStep("pdfOptions");
		} finally {
			setIsLoading(false);
		}
	};

	// --- SAVE ---

	const handleFinalUpload = async () => {
		let filesToUpload = [];
		let uploadMetadata = {
			content_set_id: selectedContentSet?.id,
			media_mode: mediaMode,
		};

		if (mediaMode === "MEDIA") {
			// Logica Monitor
			if (step === "imageUpload" || step === "videoUpload") {
				filesToUpload = files;
			} else if (step === "pdfPreview") {
				// Logica specifica per PDF estratto
				filesToUpload = extractedImages
					.filter((img) => selectedImages.includes(img.id))
					.map((img) => img.file);
			}

			if (filesToUpload.length === 0) {
				setError("Nessun file selezionato.");
				return;
			}
		} else {
			// Logica Extra
			uploadMetadata = {
				...uploadMetadata,
				extra_config: {
					...extraConfig,
					target_counters: extraConfig.counters.join(","),
					target_articles: extraConfig.articles.join(","),
				},
				type: extraType,
			};
			if (extraType === "image") {
				if (files.length === 0) {
					setError("Seleziona un'immagine.");
					return;
				}
				filesToUpload = files;
			} else {
				if (!extraText.trim()) {
					setError("Inserisci testo.");
					return;
				}
				filesToUpload = [
					new File(
						[new Blob([extraText], { type: "text/plain" })],
						"message.txt",
						{ type: "text/plain" },
					),
				];
				uploadMetadata["extra_config"]["value"] = extraText;
			}
		}

		await onSave(filesToUpload, uploadMetadata);
		handleClose();
	};

	// --- RENDERING DEI PASSAGGI ---

	const renderContentSetStep = () => (
		<Box
			sx={{
				p: 4,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				height: "100%",
			}}
		>
			<Box sx={{ mb: 4, textAlign: "center" }}>
				<FolderIcon
					sx={{ fontSize: 60, color: "primary.main", mb: 2, opacity: 0.8 }}
				/>
				<Typography variant="h5" fontWeight={700} gutterBottom>
					Scegli la Raccolta
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Dove vuoi salvare i nuovi contenuti?
				</Typography>
			</Box>
			{showNewContentSet ? (
				<Paper
					elevation={0}
					sx={{
						p: 3,
						width: "100%",
						maxWidth: 400,
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: 3,
					}}
				>
					<Typography variant="subtitle2" fontWeight={700} mb={1}>
						Nuova Raccolta
					</Typography>
					<TextField
						fullWidth
						placeholder="Nome raccolta..."
						value={newContentSetName}
						onChange={(e) => setNewContentSetName(e.target.value)}
						size="small"
						sx={{ mb: 2 }}
						autoFocus
					/>
					<Stack direction="row" spacing={1} justifyContent="flex-end">
						<Button onClick={() => setShowNewContentSet(false)} color="inherit">
							Annulla
						</Button>
						<Button
							variant="contained"
							onClick={handleCreateContentSet}
							disabled={!newContentSetName.trim()}
						>
							Crea
						</Button>
					</Stack>
				</Paper>
			) : (
				<Stack spacing={2} sx={{ width: "100%", maxWidth: 400 }}>
					<Autocomplete
						options={contentSets}
						getOptionLabel={(option) => option.content_set_name || ""}
						value={selectedContentSet}
						onChange={(event, newValue) => setSelectedContentSet(newValue)}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Seleziona Raccolta"
								variant="outlined"
								sx={{ bgcolor: "background.paper" }}
							/>
						)}
					/>
					<Button
						startIcon={<AddCircleOutlineIcon />}
						onClick={() => setShowNewContentSet(true)}
						color="primary"
					>
						Crea Nuova Raccolta
					</Button>
				</Stack>
			)}
			<Box
				sx={{
					mt: "auto",
					pt: 4,
					width: "100%",
					display: "flex",
					justifyContent: "flex-end",
				}}
			>
				<Button
					variant="contained"
					size="large"
					onClick={() =>
						setStep(mediaMode === "MEDIA" ? "selection" : "extraSelection")
					}
					disabled={!selectedContentSet}
					sx={{ borderRadius: 2, px: 4 }}
				>
					Continua
				</Button>
			</Box>
		</Box>
	);

	const renderSelectionStep = () => (
		<Box
			sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
		>
			<Button
				startIcon={<ArrowBackIcon />}
				onClick={() => setStep("contentSet")}
				sx={{ alignSelf: "flex-start", mb: 2, color: "text.secondary" }}
			>
				Indietro
			</Button>
			<Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom>
				Tipo di Contenuto
			</Typography>

			<Grid container spacing={3} sx={{ flexGrow: 1, alignItems: "center" }}>
				<Grid item size={{ xs: 4 }} sx={{ height: 220 }}>
					<SelectionCard
						icon={<PhotoLibraryIcon />}
						title="Immagini"
						description="JPG, PNG"
						onClick={() => setStep("imageUpload")}
					/>
				</Grid>
				<Grid item size={{ xs: 4 }} sx={{ height: 220 }}>
					<SelectionCard
						icon={<VideoLibraryIcon />}
						title="Video"
						description="MP4"
						onClick={() => setStep("videoUpload")}
						color="secondary"
					/>
				</Grid>
				<Grid item size={{ xs: 4 }} sx={{ height: 220 }}>
					<SelectionCard
						icon={<PictureAsPdfIcon />}
						title="PDF"
						description="Documenti"
						onClick={() => setStep("pdfUpload")}
						color="error"
					/>
				</Grid>
			</Grid>
		</Box>
	);

	const renderExtraSelectionStep = () => (
		<Box
			sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
		>
			<Button
				startIcon={<ArrowBackIcon />}
				onClick={() => setStep("contentSet")}
				sx={{ alignSelf: "flex-start", mb: 2, color: "text.secondary" }}
			>
				Indietro
			</Button>
			<Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom>
				Formato Contenuto Extra
			</Typography>
			<Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
				<Grid item size={{ xs: 6 }} sx={{ height: 240 }}>
					<SelectionCard
						icon={<PhotoLibraryIcon />}
						title="Immagine Promozionale"
						description="Carica un banner grafico"
						onClick={() => {
							setExtraType("image");
							setStep("extraInput");
						}}
					/>
				</Grid>
				<Grid item size={{ xs: 6 }} sx={{ height: 240 }}>
					<SelectionCard
						icon={<TextFieldsIcon />}
						title="Messaggio Testuale"
						description="Scrivi un avviso o una nota"
						onClick={() => {
							setExtraType("text");
							setStep("extraInput");
						}}
						color="warning"
					/>
				</Grid>
			</Grid>
		</Box>
	);

	const renderFileUploadStep = (accept, mode) => {
		const hasFiles = files.length > 0 && mode !== "pdf";

		// Funzione helper per creare URL anteprima sicuri
		const getPreview = (file) => {
			try {
				return URL.createObjectURL(file);
			} catch (e) {
				return null;
			}
		};

		return (
			<Box
				sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
			>
				{/* Header Navigazione */}
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={() => {
						setStep("selection");
						setFiles([]);
					}}
					sx={{ alignSelf: "flex-start", mb: 2, color: "text.secondary" }}
				>
					Indietro
				</Button>

				{/* CASO 1: Nessun file caricato - Mostra Dropzone Grande */}
				{!hasFiles ? (
					<Dropzone
						onDrop={mode === "pdf" ? handlePdfSelected : handleFilesDrop}
						accept={accept}
						isPdf={mode === "pdf"}
						inputRef={inputRef}
					/>
				) : (
					/* CASO 2: File presenti - Mostra Griglia Anteprime e nascondi Dropzone */
					<Box
						sx={{
							flexGrow: 1,
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
						}}
					>
						{/* Toolbar azioni */}
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{ mb: 2 }}
						>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{ display: "flex", alignItems: "center", gap: 1 }}
							>
								<PhotoLibraryIcon color="primary" />
								{files.length}{" "}
								{files.length === 1 ? "immagine pronta" : "immagini pronte"}
							</Typography>

							<Button
								variant="outlined"
								startIcon={<AddCircleOutlineIcon />}
								onClick={() => inputRef.current && inputRef.current.click()}
							>
								Aggiungi altre
							</Button>
						</Stack>

						{/* Area Griglia Scrollabile */}
						<Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
							<Grid container spacing={2}>
								{files.map((file, i) => (
									<Grid item xs={6} sm={4} key={i}>
										<Paper
											elevation={0}
											variant="outlined"
											sx={{
												position: "relative",
												borderRadius: 2,
												overflow: "hidden",
												height: 180,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												bgcolor: "grey.50",
												"&:hover .delete-btn": { opacity: 1 },
											}}
										>
											{/* Anteprima Immagine */}
											{file.type.startsWith("image/") ? (
												<Box
													component="img"
													src={getPreview(file)}
													alt={file.name}
													sx={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
													}}
												/>
											) : (
												// Fallback per video o altri file se non sono immagini
												<Box sx={{ textAlign: "center", p: 1 }}>
													<VideoLibraryIcon
														sx={{
															fontSize: 40,
															color: "text.secondary",
															mb: 1,
														}}
													/>
													<Typography
														variant="caption"
														display="block"
														sx={{ lineClamp: 2, overflow: "hidden" }}
													>
														{file.name}
													</Typography>
												</Box>
											)}

											{/* Overlay Info Dimensione */}
											<Box
												sx={{
													position: "absolute",
													bottom: 0,
													left: 0,
													right: 0,
													bgcolor: alpha(theme.palette.common.black, 0.6),
													color: "white",
													p: 0.5,
													textAlign: "center",
												}}
											>
												<Typography variant="caption">
													{(file.size / 1024).toFixed(0)} KB
												</Typography>
											</Box>

											{/* Tasto Cancella Flottante */}
											<IconButton
												className="delete-btn"
												size="small"
												onClick={() =>
													setFiles((p) => p.filter((f) => f !== file))
												}
												sx={{
													position: "absolute",
													top: 5,
													right: 5,
													bgcolor: "error.main",
													color: "white",
													opacity: 0,
													transition: "opacity 0.2s",
													"&:hover": { bgcolor: "error.dark" },
												}}
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</Paper>
									</Grid>
								))}
							</Grid>
						</Box>

						{/* Dropzone Nascosta (necessaria per mantenere vivo l'inputRef) */}
						<Box sx={{ display: "none" }}>
							<Dropzone
								onDrop={handleFilesDrop}
								accept={accept}
								inputRef={inputRef}
							/>
						</Box>
					</Box>
				)}
			</Box>
		);
	};

	const renderExtraConfigStep = () => (
		<Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
			<Stack direction="row" alignItems="center" spacing={2} mb={3}>
				<IconButton
					onClick={() => setStep("extraInput")}
					size="small"
					sx={{ border: `1px solid ${theme.palette.divider}` }}
				>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h6" fontWeight={700}>
					Configurazione Regole
				</Typography>
			</Stack>

			<SectionBox title="Trigger Stampa" icon={<TuneIcon fontSize="small" />}>
				<ToggleButtonGroup
					value={extraConfig.trigger}
					exclusive
					color="primary"
					fullWidth
					sx={{ mt: 1 }}
					onChange={(e, val) =>
						val && setExtraConfig((p) => ({ ...p, trigger: val }))
					}
				>
					<ToggleButton value={1} sx={{ textTransform: "none", py: 1.5 }}>
						<TicketIcon sx={{ mr: 1 }} /> Prenotazione Ticket
					</ToggleButton>
					<ToggleButton value={2} sx={{ textTransform: "none", py: 1.5 }}>
						<RestaurantIcon sx={{ mr: 1 }} /> Stampa Ingredienti
					</ToggleButton>
				</ToggleButtonGroup>
			</SectionBox>

			<SectionBox
				title="Target Specifici"
				icon={<StorefrontIcon fontSize="small" />}
			>
				<Grid container spacing={2}>
					<Grid item size={{ xs: 6 }}>
						<Autocomplete
							multiple
							freeSolo
							options={[]}
							value={extraConfig.counters}
							onChange={(e, v) =>
								setExtraConfig((p) => ({ ...p, counters: v }))
							}
							renderTags={(val, getProps) =>
								val.map((opt, i) => (
									<Chip size="small" label={opt} {...getProps({ index: i })} />
								))
							}
							renderInput={(p) => (
								<TextField
									{...p}
									label="Codici Banco"
									placeholder="Invio per aggiungere"
									size="small"
								/>
							)}
						/>
					</Grid>
					<Grid item size={{ xs: 6 }}>
						<Autocomplete
							multiple
							freeSolo
							options={[]}
							value={extraConfig.articles}
							onChange={(e, v) =>
								setExtraConfig((p) => ({ ...p, articles: v }))
							}
							renderTags={(val, getProps) =>
								val.map((opt, i) => (
									<Chip size="small" label={opt} {...getProps({ index: i })} />
								))
							}
							renderInput={(p) => (
								<TextField
									{...p}
									label="Codici Articolo"
									placeholder="Invio per aggiungere"
									size="small"
									InputProps={{
										...p.InputProps,
										startAdornment: (
											<>
												<InputAdornment position="start">
													<ArticleIcon fontSize="small" />
												</InputAdornment>
												{p.InputProps.startAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</Grid>
				</Grid>
			</SectionBox>

			<SectionBox
				title="Priorità e Code"
				icon={<PriorityIcon fontSize="small" />}
			>
				<Grid container spacing={2}>
					<Grid item size={{ xs: 6 }}>
						<TextField
							fullWidth
							type="number"
							label="Priorità"
							value={extraConfig.priority}
							onChange={(e) =>
								setExtraConfig((p) => ({ ...p, priority: e.target.value }))
							}
							size="small"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">#</InputAdornment>
								),
							}}
							helperText="1 = Massima"
						/>
					</Grid>
					<Grid item size={{ xs: 6 }}>
						<TextField
							fullWidth
							type="number"
							label="Soglia Coda"
							value={extraConfig.queueThreshold}
							onChange={(e) =>
								setExtraConfig((p) => ({
									...p,
									queueThreshold: e.target.value,
								}))
							}
							size="small"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<QueueIcon fontSize="small" />
									</InputAdornment>
								),
							}}
							helperText="Min. persone in coda"
						/>
					</Grid>
				</Grid>
			</SectionBox>
		</Box>
	);

	const renderStep = () => {
		switch (step) {
			case "contentSet":
				return renderContentSetStep();
			case "selection":
				return renderSelectionStep();
			case "imageUpload":
				return renderFileUploadStep("image/jpg,image/jpeg,image/png", "image");
			case "videoUpload":
				return renderFileUploadStep("video/mp4", "video");
			case "pdfUpload":
				return renderFileUploadStep("application/pdf", "pdf");

			case "pdfOptions":
				return (
					<Box sx={{ p: 4, textAlign: "center" }}>
						<Button
							startIcon={<ArrowBackIcon />}
							onClick={() => setStep("pdfUpload")}
							sx={{ mb: 2, alignSelf: "flex-start" }}
						>
							Indietro
						</Button>
						<Typography variant="h6" gutterBottom>
							File PDF: {pdfFile?.name}
						</Typography>
						<Stack spacing={2} mt={4} direction="row" justifyContent="center">
							<Button
								variant="outlined"
								size="large"
								startIcon={<ImageSearchIcon />}
								onClick={() => handlePdfExtract("perImage")}
							>
								Estrai Immagini
							</Button>
							<Button
								variant="contained"
								size="large"
								startIcon={<PictureAsPdfIcon />}
								onClick={() => handlePdfExtract("perPage")}
							>
								Converti Pagine
							</Button>
						</Stack>
					</Box>
				);

			case "pdfPreview":
				return (
					<Box sx={{ p: 2 }}>
						<Button
							startIcon={<ArrowBackIcon />}
							onClick={() => {
								setStep("pdfOptions");
								setExtractedImages([]);
							}}
							sx={{ mb: 2 }}
						>
							Indietro
						</Button>
						<Typography variant="subtitle2" gutterBottom>
							Seleziona le immagini da importare:
						</Typography>
						<Grid
							container
							spacing={2}
							sx={{ maxHeight: 400, overflowY: "auto" }}
						>
							{extractedImages.map((image) => (
								<Grid item size={{ xs: 6, sm: 4, md: 3 }} key={image.id}>
									<Paper
										variant="outlined"
										sx={{
											position: "relative",
											overflow: "hidden",
											height: 160,
										}}
									>
										{/* Checkbox overlay come nel vecchio codice per garantire clickabilità */}
										<FormControlLabel
											control={
												<Checkbox
													checked={selectedImages.includes(image.id)}
													onChange={() =>
														setSelectedImages((p) =>
															p.includes(image.id)
																? p.filter((x) => x !== image.id)
																: [...p, image.id],
														)
													}
												/>
											}
											label=""
											sx={{
												position: "absolute",
												top: 0,
												left: 0,
												zIndex: 1,
												width: "100%",
												height: "100%",
												m: 0,
											}}
										/>
										<Box
											component="img"
											src={image.url}
											sx={{
												width: "100%",
												height: "100%",
												objectFit: "contain",
												bgcolor: "grey.100",
												display: "block",
												p: 1,
											}}
										/>
									</Paper>
								</Grid>
							))}
						</Grid>
					</Box>
				);

			case "extraSelection":
				return renderExtraSelectionStep();
			case "extraInput":
				return (
					<Box
						sx={{
							p: 3,
							height: "100%",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Button
							startIcon={<ArrowBackIcon />}
							onClick={() => setStep("extraSelection")}
							sx={{ mb: 2, alignSelf: "flex-start", color: "text.secondary" }}
						>
							Indietro
						</Button>

						<Typography variant="h6" fontWeight={700} gutterBottom>
							{extraType === "image"
								? "Carica Immagine Promozionale"
								: "Inserisci Messaggio"}
						</Typography>

						<Box
							sx={{
								flexGrow: 1,
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
							}}
						>
							{extraType === "image" ? (
								files.length > 0 ? (
									// --- FIX: SE C'È IL FILE, MOSTRA ANTEPRIMA ---
									<Paper
										elevation={0}
										variant="outlined"
										sx={{
											p: 2,
											borderRadius: 3,
											textAlign: "center",
											bgcolor: "grey.50",
											position: "relative",
											overflow: "hidden",
										}}
									>
										<Box
											component="img"
											src={URL.createObjectURL(files[0])}
											sx={{
												maxHeight: 300,
												maxWidth: "100%",
												borderRadius: 2,
												mb: 2,
												display: "block",
												mx: "auto",
											}}
										/>
										<Typography variant="body2" fontWeight={700}>
											{files[0].name}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{(files[0].size / 1024).toFixed(0)} KB
										</Typography>

										<Button
											color="error"
											startIcon={<DeleteIcon />}
											onClick={() => setFiles([])} // Tasto per rimuovere e far tornare la Dropzone
											sx={{ mt: 2, display: "flex", mx: "auto" }}
										>
											Rimuovi Immagine
										</Button>
									</Paper>
								) : (
									// --- ALTRIMENTI MOSTRA DROPZONE ---
									<Dropzone
										onDrop={handleFilesDrop}
										accept="image/*"
										inputRef={inputRef}
										maxFiles={1}
									/>
								)
							) : (
								<TextField
									fullWidth
									multiline
									rows={6}
									label="Messaggio Testuale"
									placeholder="Scrivi qui il messaggio che apparirà sullo schermo..."
									value={extraText}
									onChange={(e) => setExtraText(e.target.value)}
									variant="outlined"
								/>
							)}
						</Box>

						<Box mt={3} display="flex" justifyContent="flex-end">
							<Button
								variant="contained"
								size="large"
								onClick={() => setStep("extraConfig")}
								// Il bottone si abilita se c'è un file (image) o del testo (text)
								disabled={
									(extraType === "image" && !files.length) ||
									(extraType === "text" && !extraText)
								}
							>
								Avanti
							</Button>
						</Box>
					</Box>
				);
			case "extraConfig":
				return renderExtraConfigStep();
			default:
				return null;
		}
	};

	const showSaveBtn =
		(mediaMode === "MEDIA" &&
			((step === "imageUpload" && files.length > 0) ||
				(step === "videoUpload" && files.length > 0) ||
				(step === "pdfPreview" && selectedImages.length > 0))) ||
		(mediaMode === "EXTRA" && step === "extraConfig");

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="md"
			PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
		>
			<DialogTitle
				sx={{
					px: 3,
					py: 2,
					borderBottom: `1px solid ${theme.palette.divider}`,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography variant="h6" fontWeight={700}>
					{mediaMode === "MEDIA" ? "Carica Media" : "Contenuti Extra"}
				</Typography>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					p: 0,
					minHeight: 500,
					bgcolor: "background.paper",
					position: "relative",
				}}
			>
				{error && (
					<Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
						{error}
					</Alert>
				)}
				{isLoading ? (
					<WaitingDialog open={true} message={loadingMessage} />
				) : (
					<Fade in>{renderStep()}</Fade>
				)}
			</DialogContent>

			{showSaveBtn && (
				<DialogActions
					sx={{
						p: 2,
						borderTop: `1px solid ${theme.palette.divider}`,
						bgcolor: "grey.50",
					}}
				>
					<Typography
						variant="caption"
						sx={{ flexGrow: 1, ml: 1, color: "text.secondary" }}
					>
						{mediaMode === "MEDIA" && step !== "pdfPreview"
							? `${files.length} file pronti`
							: ""}
						{mediaMode === "MEDIA" && step === "pdfPreview"
							? `${selectedImages.length} pagine selezionate`
							: ""}
					</Typography>
					<Button onClick={handleClose} sx={{ color: "text.secondary" }}>
						Annulla
					</Button>
					<Button
						variant="contained"
						onClick={handleFinalUpload}
						startIcon={<CloudUploadIcon />}
						sx={{ px: 3, borderRadius: 2 }}
					>
						{mediaMode === "EXTRA"
							? "Salva Configurazione"
							: "Avvia Caricamento"}
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
}
